import { tv } from 'tailwind-variants';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { cn } from '@/app_lib/utils/utils';

const inputVariants = tv({
  base: 'border rounded-sm stroke-helper-2 active:stroke-accent bg-pure',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    size: {
      lg: 'typography-default input-p-lg w-full',
      md: 'typography-sm input-p-md w-full',
      sm: 'typography-xs input-p-sm',
    },
    variant: {
      disabled: 'button-secondary-disabled cursor-not-allowed text-helper',
      error: 'stroke-red',
    },
  },
});

const labelVariants = tv({
  base: 'flex gap-xs typography-sm',
  variants: {
    variant: {
      error: 'text-red',
    },
  },
});

export type AllowedInputType =
  | 'button'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'month'
  | 'number'
  | 'password'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export interface InputV2Icon {
  element: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}

export interface InputV2Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string | boolean;
  className?: string;
  type?: AllowedInputType;
  icon?: InputV2Icon;
  dataCy?: string;
}

export const InputV2 = ({
  label,
  id,
  value,
  onChange,
  placeholder,
  error,
  required,
  className,
  disabled = false,
  type = 'text',
  icon,
  dataCy,
  ...rest
}: InputV2Props) => {
  const hasError = Boolean(error);
  const errorMessage = typeof error === 'string' ? error : undefined;
  const inputVariant = (disabled && 'disabled') || (hasError && 'error') || undefined;

  const inputElement = (
    <input
      aria-describedby={errorMessage ? `${id}-error` : undefined}
      aria-invalid={hasError}
      className={cn(inputVariants({ variant: inputVariant }), icon && 'w-full pr-10')}
      data-cy={dataCy}
      disabled={disabled}
      id={id}
      name={id}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      type={type}
      {...(value !== undefined ? { value } : {})}
      {...rest}
    />
  );

  return (
    <div className={cn('flex flex-col gap-xs', className)}>
      <label
        className={labelVariants({ variant: hasError ? 'error' : undefined })}
        data-cy={`${dataCy}-label`}
        htmlFor={id}
      >
        {label}
        {required && (
          <span
            aria-hidden='true'
            className='icon-red select-none'
          >
            *
          </span>
        )}
      </label>
      {icon ? (
        <div className='relative flex items-center'>
          {inputElement}
          <ActionableIcon
            aria-label={icon.ariaLabel}
            className='icon-secondary min-h-[unset] min-w-[unset] absolute right-3 flex items-center cursor-pointer hover:text-accent pointer-events-auto'
            data-cy={`${dataCy}-icon`}
            onClick={icon.onClick}
            type='button'
          >
            {icon.element}
          </ActionableIcon>
        </div>
      ) : (
        inputElement
      )}
      {errorMessage && (
        <p
          className='text-red typography-xs'
          id={`${id}-error`}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
